import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/home';
import About from './pages/common-components/About/About';
import Contact from './pages/common-components/Contact/Contact';
import Blog from './pages/common-components/Blog/Blog';
import BlogView from './pages/common-components/Blog/BlogView';
import Shop from './pages/common-components/Shop/Shop';
import Cart from './pages/common-components/Cart/Cart';
import { CompanyProvider } from './pages/Contexts/CompanyContext';
import { AuthProvider } from './pages/Contexts/AuthContext';
import Login from './pages/common-components/Auth/Login';
import Signup from './pages/common-components/Auth/Signup';
import ForgotPassword from './pages/common-components/Auth/ForgotPassword';
import Account from './pages/common-components/Auth/Account';
import { CompanyProfileProvider, useCompanyProfile } from './pages/Contexts/CompanyProfileContext';
import { CategoryProvider } from './pages/Contexts/CategoryContext';
import { BrandProvider } from './pages/Contexts/BrandContext';
import { AppLoadingProvider, useAppLoading } from './pages/Contexts/AppLoadingContext';
import { ToastProvider } from './pages/Contexts/ToastContext';
import AppLoader from './pages/common-components/App-Loader/AppLoader';
import ProductDetails from './pages/common-components/Product-details/ProductDetails';
import Checkout from './pages/common-components/Checkout/Checkout';
import OrderConfirmation from './pages/common-components/Checkout/OrderConfirmation';
import ScrollToTop from './pages/common-components/Back-to-top/ScrollToTop';
import ClientAIChat from './pages/common-components/AIChat/ClientAIChat';
import OrderTracking from './pages/common-components/Ordertracking/Ordertracking';
import ShippingRates from './pages/common-components/Shipping-Rates/Shippingrates';
import FAQs from './pages/common-components/Faqs/Faqs';
import OrderReturn from './pages/common-components/Orderreturn/Orderreturn';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './pages/Lib/queryClient';

// ── Logo imported once here, passed as a prop to AppLoader ───────────────────
import logoImage from './logo.png';
import TermsAndConditions from './pages/common-components/Terms-and-Conditions/Termsandconditions';
import PrivacyPolicy from './pages/common-components/Privacy-Policy/Privacypolicy';
// Update this path to match your actual logo file, e.g.:
//   import logoImage from './assets/logo.svg';
//   import logoImage from './assets/images/brand-logo.png';
// ─────────────────────────────────────────────────────────────────────────────

// ── Watches CompanyProfile loading and hides the loader when ready ────────────

const AppReadyGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { loading } = useCompanyProfile();
  const { setAppLoading } = useAppLoading();

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setAppLoading(false), 400);
      return () => clearTimeout(timer);
    }
  }, [loading, setAppLoading]);

  return <>{children}</>;
};

// ── Loader overlay — logo and company name passed in as props ─────────────────

const LoaderOverlay: React.FC = () => {
  const { isAppLoading } = useAppLoading();
  const { companyProfile } = useCompanyProfile();

  if (!isAppLoading) return null;

  return (
    <AppLoader
      logoImage={logoImage}
      companyName={companyProfile?.companyName}
    />
  );
};

// ── App ───────────────────────────────────────────────────────────────────────

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <CompanyProvider>
          <CompanyProfileProvider>
            <CategoryProvider>
              <BrandProvider>
                <AppLoadingProvider>
                  <AuthProvider>
                    <AppReadyGate>

                      <LoaderOverlay />

                      <Router>
                        <ScrollToTop />
                        <ClientAIChat />
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/shop" element={<Shop />} />
                          <Route path="/blog/:slug" element={<BlogView />} />
                          <Route path="/about" element={<About />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/cart" element={<Cart />} />
                          <Route path="/product/:id" element={<ProductDetails />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/order-tracking" element={<OrderTracking />} />
                          <Route path="/shipping" element={<ShippingRates />} />
                          <Route path="/returns" element={<OrderReturn />} />
                          <Route path="/faqs" element={<FAQs />} />
                          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/account" element={<Account />} />
                        </Routes>
                      </Router>

                    </AppReadyGate>
                  </AuthProvider>
                </AppLoadingProvider>
              </BrandProvider>
            </CategoryProvider>
          </CompanyProfileProvider>
        </CompanyProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;